import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Loader2, AlertCircle, Search, UserPlus, X } from 'lucide-react';
import { api } from '../context/AuthContext';

const BestandSuche = ({ onWahl, onNeu }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get('/cases/neuer-chat/suche', { params: { q: q.trim() } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 border border-gray-200 px-3 py-2 focus-within:border-[#b8893d]">
        <Search className="w-4 h-4 text-[#7a8a99]" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Vorhandenen Mandanten suchen: Name, Mandantennummer, E-Mail…" className="flex-1 text-sm focus:outline-none" data-testid="new-case-mandant-search" />
      </div>
      <ul className="max-h-56 overflow-y-auto border border-gray-100 divide-y divide-gray-50" data-testid="new-case-mandant-liste">
        {items === null && <li className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-[#b8893d]" /></li>}
        {items?.length === 0 && <li className="py-4 text-center text-xs text-[#7a8a99]">Kein Mandant gefunden.</li>}
        {items?.map((m) => (
          <li key={m.mandanten_nr}>
            <button type="button" onClick={() => onWahl(m)} data-testid={`new-case-mandant-${m.mandanten_nr}`} className="w-full text-left px-3 py-2 hover:bg-[#fbf7f0] transition-colors">
              <span className="text-sm text-[#1B3A5C]">{m.name}</span>
              <span className="block text-[11px] font-mono text-[#7a8a99]">M-{m.mandanten_nr}{m.email ? ` · ${m.email}` : ' · keine E-Mail'} · {m.akten.length} {m.akten.length === 1 ? 'Akte' : 'Akten'}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" onClick={onNeu} data-testid="new-case-neuer-mandant" className="inline-flex items-center gap-1.5 text-xs text-[#b8893d] hover:text-[#1B3A5C]">
        <UserPlus className="w-3.5 h-3.5" /> Mandant existiert noch nicht – neu anlegen
      </button>
    </div>
  );
};

export const NewCaseModal = ({ onClose, onCreated, mnr, neuerMandant = false }) => {
  const RECHTSGEBIETE = [
    'Ausländerrecht', 'Strafrecht', 'Steuerberatung', 'Steuerrecht',
    'Gesellschaftsrecht', 'Verwaltungsrecht', 'Mietrecht', 'Verkehrsrecht', 'Sonstiges',
  ];
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', companyName: '', phone: '', city: '',
    rechtsgebiet: 'Ausländerrecht', description: '',
    mandanten_nr: '', aktenzeichen: '',
  });
  const [createLogin, setCreateLogin] = useState(true);
  const [mitAkte, setMitAkte] = useState(!neuerMandant);
  const [bestand, setBestand] = useState(null);
  const [neu, setNeu] = useState(neuerMandant);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => {
    if (!mnr) return;
    api.get('/cases/neuer-chat/suche', { params: { q: mnr } }).then((r) => { const m = (r.data.items || []).find((x) => x.mandanten_nr === mnr); if (m) setBestand(m); }).catch(() => {});
  }, [mnr]);

  const ohneAkte = neu && !bestand && !mitAkte;
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!bestand && !neu) { setError('Bitte zuerst einen Mandanten auswählen oder neu anlegen'); return; }
    if (!bestand && (!form.email.trim() || !form.lastName.trim())) {
      setError('E-Mail und Nachname sind Pflichtfelder');
      return;
    }
    if (ohneAkte && !form.mandanten_nr.trim()) { setError('Ohne Akte bitte die Mandantennummer aus DATEV angeben'); return; }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, (v || '').trim()])
      );
      const r = await api.post('/cases', bestand
        ? { bestand_mnr: bestand.mandanten_nr, rechtsgebiet: payload.rechtsgebiet, description: payload.description, aktenzeichen: payload.aktenzeichen, create_login: false }
        : { ...payload, ...(ohneAkte ? { aktenzeichen: '', ohne_akte: true } : {}), create_login: createLogin });
      onCreated?.(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || (ohneAkte ? 'Mandant konnte nicht angelegt werden' : 'Akte konnte nicht angelegt werden'));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-case-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-t-4 border-[#b8893d] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <p className="text-[10px] tracking-[0.3em] text-[#b8893d]">KANZLEI-INTERN</p>
            <h2 className="font-serif text-xl text-[#1B3A5C]">{neuerMandant ? 'Neuen Mandanten anlegen' : 'Neue Akte anlegen'}</h2>
            {neuerMandant && <p className="text-xs text-[#34536b] mt-1">Legt den Mandanten an – eine erste Akte ist optional und kann auch später im Mandanten angelegt werden.</p>}
          </div>
          <button type="button" onClick={onClose} className="text-[#34536b] hover:text-rose-600 text-2xl leading-none" data-testid="close-new-case-modal">×</button>
        </header>

        <div className="p-5 space-y-4">
          {/* Mandant */}
          <div>
            <p className="text-[10px] tracking-[0.2em] text-[#34536b] mb-2">MANDANT</p>
            {bestand ? (
              <div className="flex items-center gap-3 px-3 py-2.5 bg-[#fbf7f0] border-l-4 border-[#b8893d]" data-testid="new-case-bestand">
                <div className="min-w-0 flex-1"><p className="text-sm text-[#1B3A5C] font-medium">{bestand.name}</p>
                  <p className="text-[11px] font-mono text-[#7a8a99]">M-{bestand.mandanten_nr}{bestand.email ? ` · ${bestand.email}` : ''} · bisher {bestand.akten.length} {bestand.akten.length === 1 ? 'Akte' : 'Akten'}</p></div>
                <button type="button" onClick={() => setBestand(null)} aria-label="Andere Auswahl" data-testid="new-case-bestand-entfernen"><X className="w-4 h-4 text-[#7a8a99] hover:text-rose-600" /></button>
              </div>
            ) : !neu ? <BestandSuche onWahl={(m) => { setBestand(m); setError(''); }} onNeu={() => setNeu(true)} /> : (<>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={form.firstName} onChange={onChange('firstName')} placeholder="Vorname"
                data-testid="new-case-firstName"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <input value={form.lastName} onChange={onChange('lastName')} placeholder="Nachname *"
                data-testid="new-case-lastName" required
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <input value={form.email} onChange={onChange('email')} placeholder="E-Mail *" type="email"
                data-testid="new-case-email" required
                className="md:col-span-2 px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <input value={form.phone} onChange={onChange('phone')} placeholder="Telefon"
                data-testid="new-case-phone"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <input value={form.city} onChange={onChange('city')} placeholder="Stadt"
                data-testid="new-case-city"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <input value={form.companyName} onChange={onChange('companyName')} placeholder="Firmenname (optional)"
                data-testid="new-case-companyName"
                className="md:col-span-2 px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
            </div>
            <button type="button" onClick={() => setNeu(false)} className="mt-2 text-xs text-[#b8893d] hover:text-[#1B3A5C]" data-testid="new-case-zur-suche">← Doch vorhandenen Mandanten suchen</button>
            </>)}
          </div>

          {/* Akte */}
          {neu && !bestand && (
            <label className="flex items-center justify-between gap-3 p-3 border border-gray-200 cursor-pointer hover:border-[#b8893d] transition-colors duration-200" data-testid="new-case-mit-akte-label">
              <span className="text-xs text-[#1B3A5C]"><span className="font-semibold">Erste Akte gleich anlegen</span><br /><span className="text-[#34536b]">{mitAkte ? 'Rechtsgebiet und Aktenzeichen unten angeben.' : 'Nur Stammdaten und Mandantennummer – Akte später anlegen.'}</span></span>
              <input type="checkbox" checked={mitAkte} onChange={(e) => setMitAkte(e.target.checked)} data-testid="new-case-mit-akte" className="w-4 h-4 accent-[#b8893d]" />
            </label>
          )}
          {!ohneAkte && <div>
            <p className="text-[10px] tracking-[0.2em] text-[#34536b] mb-2">AKTE</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select value={form.rechtsgebiet} onChange={onChange('rechtsgebiet')} data-testid="new-case-rechtsgebiet"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none bg-white">
                {RECHTSGEBIETE.map((rg) => <option key={rg} value={rg}>{rg}</option>)}
              </select>
              <input value={form.description} onChange={onChange('description')} placeholder="Kurzbeschreibung"
                data-testid="new-case-description"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
            </div>
          </div>}
          <div>
            <p className="text-[10px] tracking-[0.2em] text-[#34536b] mb-2">
              NUMMERN <span className="text-[#34536b]/60 normal-case tracking-normal">(aus DATEV, optional)</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {!bestand && <input value={form.mandanten_nr} onChange={onChange('mandanten_nr')} placeholder="Mandantennummer aus DATEV (4XXXX)" pattern="4\d{4}" title="5-stellige Mandantennummer, beginnt mit 4 (z. B. 40017)" inputMode="numeric"
                data-testid="new-case-mandanten-nr"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none font-mono" />}
              {!ohneAkte && <input value={form.aktenzeichen} onChange={onChange('aktenzeichen')} placeholder="AZ aus DATEV (000000-26)"
                data-testid="new-case-aktenzeichen"
                className="px-3 py-2 text-sm border border-gray-200 focus:border-[#b8893d] focus:outline-none font-mono" />}
            </div>
            <p className="text-[10px] text-[#34536b]/60 mt-1">
              {bestand ? 'Aktenzeichen aus DATEV eintragen.' : ohneAkte ? 'Ohne Akte ist die Mandantennummer aus DATEV Pflicht.' : 'Mandantennummer und Aktenzeichen aus DATEV.'}
            </p>
          </div>

          {neu && !bestand && <label className="flex items-start gap-3 p-3 bg-[#fbf7f0] border-l-4 border-[#b8893d] cursor-pointer" data-testid="new-case-create-login-label">
            <input type="checkbox" checked={createLogin} onChange={(e) => setCreateLogin(e.target.checked)} data-testid="new-case-create-login" className="mt-0.5 accent-[#b8893d]" />
            <span className="text-xs text-[#1B3A5C]">
              <span className="font-semibold">Website-Zugang direkt einrichten</span><br />
              <span className="text-[#34536b]">Der Mandant erhält eine Einladung per E-Mail mit Link zum Passwort-Festlegen (7 Tage gültig) und kann sich danach im Mandantenportal anmelden und chatten. Besteht bereits ein Konto, passiert nichts.</span>
            </span>
          </label>}

          {error && (
            <div className="flex items-start gap-2 p-2.5 bg-rose-50 border-l-4 border-rose-500 text-rose-700 text-xs" data-testid="new-case-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 bg-[#f5f8fa] border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-wider text-[#34536b] hover:text-[#1B3A5C]">
            ABBRECHEN
          </button>
          <button type="submit" disabled={submitting} data-testid="new-case-submit"
            className="inline-flex items-center gap-2 px-5 py-2 text-xs tracking-[0.2em] bg-[#b8893d] hover:bg-[#a07730] text-white disabled:opacity-60">
            {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> ANLEGEN…</> : <>{neuerMandant && neu ? 'MANDANT ANLEGEN' : 'AKTE ANLEGEN'} <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
};

export default NewCaseModal;
